import React, { useEffect, useState, useRef, useCallback } from 'react';
// 👇 Naya Import
import { customFetch } from '../apiClient';

const AccountMasterForm = () => {
  const [accounts, setAccounts] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "" });
  const [newForm, setNewForm] = useState({ name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formRef = useRef(null);

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  // ✅ Updated: fetchAccounts using customFetch
  const fetchAccounts = () => {
    customFetch(`/AccountMaster`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to load accounts");
        return res.json();
      })
      .then(setAccounts)
      .catch((err) => setError(err.message));
  };

  useEffect(() => { fetchAccounts(); }, []);

  // Auto-focus
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector('input:not([disabled]):not([type="hidden"])');
      firstInput?.focus();
    }
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll('input:not([disabled]), button:not([disabled])');
    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit(); return;
    }
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) handleKeyDown(e);
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleEditClick = (account) => {
    setEditRowId(account.amId);
    setEditForm({ amId: account.amId, name: account.name });
    setError(""); setSuccess("");
  };

  const handleInputChange = (e) => setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleNewInputChange = (e) => setNewForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // ✅ Updated: handleUpdateSubmit using customFetch
  const handleUpdateSubmit = async (amId) => {
    if (!editForm.name.trim()) { setError("Account Name required."); return; }
    setLoading(true); setError(""); setSuccess("");

    try {
      const res = await customFetch(`/AccountMaster/${amId}`, {
        method: "PUT", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editForm, amId }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Update failed.");
      }
      setSuccess("Account updated successfully!");
      setEditRowId(null); fetchAccounts();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ✅ Updated: handleDelete using customFetch
  const handleDelete = async (amId) => {
    if (!window.confirm("Delete this account?")) return;
    setLoading(true); setError(""); setSuccess("");

    try {
      const res = await customFetch(`/AccountMaster/${amId}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Delete failed.");
      }
      setSuccess("Account deleted successfully!");
      if (editRowId === amId) setEditRowId(null);
      fetchAccounts();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ✅ Updated: handleAddNewSubmit using customFetch
  const handleAddNewSubmit = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) { setError("Account Name required."); return; }
    setLoading(true); setError(""); setSuccess("");

    try {
      const res = await customFetch(`/AccountMaster`, {
        method: "POST", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newForm),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Add failed.");
      }
      setSuccess("Account added successfully!");
      setNewForm({ name: "" }); fetchAccounts();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="dc-wrap">
      
      {/* Add Account Card */}
      <div className="dc-card dc-mb3">
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <i className="bi bi-plus-circle"></i> <span>Add New Account</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          <form ref={formRef} onSubmit={handleAddNewSubmit} className="dc-inline-form">
            <div className="dc-inline-input">
              <label className="dc-label">Account Name <span className="dc-required">*</span></label>
              <input name="name" type="text" className="dc-input" placeholder="e.g. Salary, Rent, Maintenance..." value={newForm.name} onChange={handleNewInputChange} disabled={loading} />
            </div>
            <button type="submit" className="dc-btn-submit" disabled={loading}>
              <i className="bi bi-plus-lg"></i> {loading ? "..." : "Add Account"}
            </button>
          </form>
        </div>
      </div>

      {/* Account List Card */}
      <div className="dc-card">
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <i className="bi bi-list-ul"></i> <span>Account Master List ({accounts.length})</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Sr No</th>
                  <th>Account Name</th>
                  <th className="dc-text-center" style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr><td colSpan="3" className="dc-empty">No accounts found.</td></tr>
                ) : (
                  accounts.map((acc, index) => (
                    <tr key={acc.amId}>
                      <td className="dc-text-muted">{index + 1}</td>
                      <td>
                        {editRowId === acc.amId ? (
                          <input type="text" name="name" value={editForm.name} onChange={handleInputChange} className="dc-input" disabled={loading} autoFocus />
                        ) : (
                          <span className="dc-fw-medium">{acc.name}</span>
                        )}
                      </td>
                      <td className="dc-text-center">
                        {editRowId === acc.amId ? (
                          <div className="dc-btn-group">
                            <button className="dc-icon-btn dc-save" onClick={() => handleUpdateSubmit(acc.amId)} disabled={loading}><i className="bi bi-check-lg"></i></button>
                            <button className="dc-icon-btn dc-cancel" onClick={() => setEditRowId(null)} disabled={loading}><i className="bi bi-x-lg"></i></button>
                          </div>
                        ) : (
                          <div className="dc-btn-group">
                            <button className="dc-icon-btn dc-edit" onClick={() => handleEditClick(acc)}><i className="bi bi-pencil-square"></i></button>
                            <button className="dc-icon-btn dc-delete" onClick={() => handleDelete(acc.amId)}><i className="bi bi-trash"></i></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ✅ PURE COMPACT CSS (ZERO BOOTSTRAP OVERRIDES) */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: hidden; }
        .dc-mb3 { margin-bottom: 12px; }
        
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        
        .dc-body { padding: 10px 12px; }

        .dc-inline-form { display: flex; gap: 8px; align-items: flex-end; }
        .dc-inline-input { flex: 1; display: flex; flex-direction: column; }
        
        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        .dc-required { color: #dc2626; }
        
        .dc-input { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }

        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; white-space: nowrap; }
        .dc-btn-submit:hover { opacity: 0.9; }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        .dc-table-wrap { overflow-x: auto; border: 1px solid #e0e6ed; border-radius: 4px; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .dc-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; text-align: left; }
        .dc-table td { padding: 5px 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .dc-table tr:hover { background: #f0f7ff; }
        
        .dc-text-center { text-align: center; }
        .dc-text-muted { color: #6c757d; }
        .dc-fw-medium { font-weight: 500; }
        .dc-empty { text-align: center; padding: 20px; color: #7f8c8d; }

        .dc-btn-group { display: flex; gap: 4px; justify-content: center; }
        .dc-icon-btn { width: 22px; height: 22px; border-radius: 3px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
        .dc-edit { background: #e0f2fe; color: #0284c7; }
        .dc-edit:hover { background: #0284c7; color: white; }
        .dc-delete { background: #fee2e2; color: #dc2626; }
        .dc-delete:hover { background: #dc2626; color: white; }
        .dc-save { background: #d1fae5; color: #059669; }
        .dc-save:hover { background: #059669; color: white; }
        .dc-cancel { background: #f3f4f6; color: #495057; }
        .dc-cancel:hover { background: #cbd5e0; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        @media (max-width: 600px) {
          .dc-inline-form { flex-direction: column; align-items: stretch; }
          .dc-btn-submit { justify-content: center; margin-top: 4px; }
        }
      `}</style>
    </div>
  );
};

export default AccountMasterForm;